import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import type { ReactNode } from 'react'
import { Fragment, useMemo, useRef } from 'react'
import type {
  HotelMapViewData,
  ExperienceMapViewData,
  TripItineraryData,
  TripStop,
  TripDay,
  TripItineraryItem,
  TripItemWithoutDay,
  ManualItineraryDeleteRequest,
  TransportConnection,
} from 'tripPlanner/api/types'
import { isFlightCardSavedItemData } from 'tripPlanner/api/types'
import { isBookedFlightOption } from './ShortlistedFlightsSection/flightShortlistUtils'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import TripPlanCanvasItem from './TripPlanCanvasItem'
import TripPlanTransportLine from './TripPlanTransportLine'
import type { ItineraryScrollTarget } from '../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../hooks/useItineraryScrollTarget'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { getCruiseSavedOfferId } from 'tripPlanner/utils/savedItemDataGuards'
import {
  CanvasContainer,
  ScrollableContainer,
  SkeletonStopSheet,
  FullWidthLoadingBox,
  DottedConnector,
  ItemSpacer,
  StopContentWrapper,
} from './TripPlanCanvas.styles'

const TRIP_PLAN_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `div[data-item-id="${target.itemId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

export type Highlight = {
  id: number
  name: string
  emoji: string
  description: string
  googlePlaceId?: string
  savedItemData?: TripItemWithoutDay['savedItemData']
}

function addCruiseItemOfferIds(
  offerIds: Set<string>,
  items: ReadonlyArray<TripItineraryItem | TripItemWithoutDay>,
) {
  for (const item of items) {
    const offerId = getCruiseSavedOfferId(item.savedItemData)
    if (offerId) offerIds.add(offerId)
  }
}

function getVisibleCruiseItemOfferIds(stops: ReadonlyArray<TripStop>): Set<string> {
  const offerIds = new Set<string>()

  for (const stop of stops) {
    for (const day of stop.days ?? []) {
      addCruiseItemOfferIds(offerIds, day.items ?? [])
    }

    addCruiseItemOfferIds(offerIds, stop.itemsWithoutDay ?? [])
  }

  return offerIds
}

function getCanvasTransport(
  transport: TransportConnection,
  visibleCruiseItemOfferIds: Set<string>,
  renderedCruiseOfferIds: Set<string>,
): TransportConnection | undefined {
  if (transport.options.length === 0) return undefined

  const filteredOptions = transport.options.filter((option) => {
    const cruiseOfferId = getCruiseSavedOfferId(option.savedItemData)
    if (!cruiseOfferId) return true

    if (visibleCruiseItemOfferIds.has(cruiseOfferId) || renderedCruiseOfferIds.has(cruiseOfferId)) {
      return false
    }

    renderedCruiseOfferIds.add(cruiseOfferId)
    return true
  })

  if (filteredOptions.length === 0) return undefined
  if (filteredOptions.length === transport.options.length) return transport

  return {
    ...transport,
    options: filteredOptions,
  }
}

interface Props {
  data?: TripItineraryData
  onLearnMoreClick?: (data: { googlePlaceId: string; placeName: string; countryName?: string; stopId?: number }) => void
  onRefetchItinerary?: () => void
  onSendMessage?: (message: string) => void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest) => Promise<void> | void
  onExploreMap: (coordinates: { lat: number; lng: number }, source: LEExploreCategorySource) => void
  onShowMap: (data: HotelMapViewData) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  enrichingStopIds?: Set<number>
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  scrollTarget?: ItineraryScrollTarget | undefined
  onScrollComplete?: () => void
}

function TripPlanCanvas(props: Props) {
  const { data, onLearnMoreClick, onRefetchItinerary, onSendMessage, onDeleteTarget, onExploreMap, onShowMap, onShowExperienceOnMap, enrichingStopIds, onAskAI, scrollTarget, onScrollComplete } = props
  const { isStreaming } = useChatStreamStatus()

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollContainerRef,
    selectors: TRIP_PLAN_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const stops = useMemo(() => data?.stops ?? [], [data])
  const visibleCruiseItemOfferIds = useMemo(() => getVisibleCruiseItemOfferIds(stops), [stops])

  const highlightsByStop = useMemo(() => {
    return stops.reduce<Record<number, Array<Highlight>>>((acc, stop: TripStop) => {
      const highlights: Array<Highlight> = []

      stop.itemsWithoutDay?.forEach((item: TripItemWithoutDay) => {
        if (item.isHighlight) {
          highlights.push({
            id: item.id,
            name: item.name,
            emoji: item.emoji,
            description: item.description,
            googlePlaceId: item.itemPlaceDetail?.placeId,
            savedItemData: item.savedItemData,
          })
        }
      })

      stop.days.forEach((day: TripDay) => {
        day.items.forEach((item: TripItineraryItem) => {
          if (item.isHighlight) {
            highlights.push({
              id: item.id,
              name: item.name,
              emoji: item.emoji,
              description: item.description,
              googlePlaceId: item.itemPlaceDetail?.placeId,
              savedItemData: item.savedItemData,
            })
          }
        })
      })

      const hasDays = stop.days.length > 0
      const hasItemsWithoutDay = (stop.itemsWithoutDay?.length ?? 0) > 0
      if (highlights.length === 0 && !hasDays && hasItemsWithoutDay) {
        stop.itemsWithoutDay?.forEach((item: TripItemWithoutDay) => {
          highlights.push({
            id: item.id,
            name: item.name,
            emoji: item.emoji,
            description: item.description,
            googlePlaceId: item.itemPlaceDetail?.placeId,
            savedItemData: item.savedItemData,
          })
        })
      }

      if (highlights.length > 0) {
        acc[stop.id] = highlights
      }

      return acc
    }, {})
  }, [stops])

  const enrichedStops = useMemo(() => {
    const renderedCruiseOfferIds = new Set<string>()

    return stops.map((stop: TripStop) => {
      const numberOfDays = stop.days?.length ?? 0
      const stopHighlights = highlightsByStop[stop.id] ?? []
      const transports = stop.transports?.flatMap((transport) => {
        const visibleTransport = getCanvasTransport(transport, visibleCruiseItemOfferIds, renderedCruiseOfferIds)
        return visibleTransport ? [visibleTransport] : []
      })

      return {
        stop,
        numberOfDays,
        stopHighlights,
        transports,
      }
    })
  }, [highlightsByStop, stops, visibleCruiseItemOfferIds])

  // A booked return flight is modelled as one transport (with a returnLeg). Surface it again
  // at the end of the itinerary as a reversed tile (return direction) so the trip reads round-trip.
  const bookedReturnTransport = useMemo((): TransportConnection | undefined => {
    for (const { transports } of enrichedStops) {
      const match = transports?.find(transport =>
        transport.options.some(option =>
          isFlightCardSavedItemData(option.savedItemData) && isBookedFlightOption(option) && Boolean(option.returnLeg),
        ),
      )
      if (match) return match
    }
    return undefined
  }, [enrichedStops])

  // Heading for the reversed return tile, built from stop place names (arrival → departure)
  // rather than reversing the display string, which is more robust to naming formats.
  const reversedReturnTitle = useMemo((): string | undefined => {
    if (!bookedReturnTransport) return undefined
    const placeNameByStopId = new Map(stops.map(stop => [stop.id, stop.placeName]))
    const from = placeNameByStopId.get(bookedReturnTransport.arrivalStopId)
    const to = placeNameByStopId.get(bookedReturnTransport.departureStopId)
    return from && to ? `${from} → ${to}` : undefined
  }, [bookedReturnTransport, stops])

  const hasStops = stops.length > 0

  if (isStreaming && !hasStops) {
    return (
      <CanvasContainer>
        <ScrollableContainer>
          <Group direction="vertical" gap={40} horizontalAlign="center">
            <SkeletonStopSheet padding={20} border="light-foreground">
              <Group direction="vertical" gap={20}>
                <Group direction="vertical" gap={12}>
                  <Group direction="horizontal" gap={12} verticalAlign="start">
                    <LoadingBox width={56} height={56} borderRadius="S" />
                    <Group direction="vertical" gap={4}>
                      <LoadingBox width={160} height={24} borderRadius="XS" />
                      <LoadingBox width={60} height={18} borderRadius="XS" />
                    </Group>
                  </Group>
                  <Group direction="vertical" gap={8}>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <Fragment key={index}>
                        <LoadingBox width={220} height={16} borderRadius="XS" />
                        <FullWidthLoadingBox height={16} borderRadius="XS" />
                      </Fragment>
                    ))}
                  </Group>
                </Group>
              </Group>
            </SkeletonStopSheet>
          </Group>
        </ScrollableContainer>
      </CanvasContainer>
    )
  }

  return (
    <CanvasContainer>
      <ScrollableContainer ref={scrollContainerRef}>
        <Group direction="vertical" gap={0} horizontalAlign="center">
          {enrichedStops.map(({ stop, numberOfDays, stopHighlights, transports }, index) => {
            const isOrigin = stop.stopType === 'ORIGIN'
            const hasTransport = transports?.some((t) => t.options.length > 0)

            const prevHadTransport = index > 0 ? (enrichedStops[index - 1].transports?.some((t) => t.options.length > 0) ?? false) : false

            // A contentless origin stop is hidden from the itinerary UI. Its data is kept
            // intact (map markers, transport options) so the first thing the customer sees
            // is the mode of transport leaving their origin. An origin that does carry
            // days/items/highlights still renders so that content is never dropped.
            const hasStopContent = (stop.days?.length ?? 0) > 0 || (stop.itemsWithoutDay?.length ?? 0) > 0 || stopHighlights.length > 0
            const hideStop = isOrigin && !hasStopContent
            let stopContent: ReactNode = null
            if (!hideStop) {
              stopContent = <TripPlanCanvasItem
                data={data}
                stopId={stop.id}
                placeName={stop.placeName}
                placeCountry={stop.placeCountry}
                numberOfDays={numberOfDays}
                description={stop.description}
                imageId={stop.imageIds?.[0] ?? ''}
                stopSeq={stop.stopSeq}
                highlights={stopHighlights}
                googlePlaceId={stop.stopPlaceDetail?.placeId}
                onLearnMoreClick={onLearnMoreClick}
                onSendMessage={onSendMessage}
                onDeleteTarget={onDeleteTarget}
                onExploreMap={onExploreMap}
                onShowMap={onShowMap}
                onShowExperienceOnMap={onShowExperienceOnMap}
                stopCoordinates={stop.stopPlaceDetail ? { lat: stop.stopPlaceDetail.lat, lng: stop.stopPlaceDetail.lng } : undefined}
                isEnriching={enrichingStopIds?.has(stop.id)}
                onAskAI={onAskAI}
                scrollTargetDayId={scrollTarget?.stopId === stop.id ? scrollTarget.dayId : undefined}
                scrollTargetItemId={scrollTarget?.stopId === stop.id ? scrollTarget.itemId : undefined}
              />
            }

            return (
              <Fragment key={stop.id}>
                {index > 0 && prevHadTransport && <DottedConnector />}
                {index > 0 && !prevHadTransport && <ItemSpacer />}
                {!hideStop && (
                  <StopContentWrapper data-stop-id={stop.id}>
                    {stopContent}
                  </StopContentWrapper>
                )}
                {hasTransport && transports?.map((transport) =>
                  transport.options.length > 0 && (
                    <Fragment key={`${transport.departureStopId}-${transport.arrivalStopId}`}>
                      {/* No connector above a hidden origin's transport — it is the first visible element */}
                      {!hideStop && <DottedConnector />}
                      <TripPlanTransportLine transport={transport} onRefetchItinerary={onRefetchItinerary} onSendMessage={onSendMessage} onDeleteTarget={onDeleteTarget} />
                    </Fragment>
                  ),
                )}
              </Fragment>
            )
          })}
          {bookedReturnTransport && (
            <>
              <DottedConnector />
              <TripPlanTransportLine
                transport={bookedReturnTransport}
                reversed
                title={reversedReturnTitle}
                onRefetchItinerary={onRefetchItinerary}
                onSendMessage={onSendMessage}
                onDeleteTarget={onDeleteTarget}
              />
            </>
          )}
        </Group>
      </ScrollableContainer>
    </CanvasContainer>
  )
}

export default TripPlanCanvas
